import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Divider, IconButton, Message, SelectPicker, Stack, Toggle, Tooltip, Whisper } from 'rsuite';

import FlagHoursModal from '../components/FlagHoursModal';
import {
  useActiveTimeOffSource,
  useCloseDates,
  useUsers,
  useUserWorkplan,
  useUserWorkplanFlags,
  useUserTimeOffDays,
  usePrefetchUserWorkplanMonths,
} from '../hooks/useQueries';
import {
  MonthSlideCells,
  getMonthSlideFetchMonths,
  getMonthSlidePrefetchMonthLists,
  monthSlideColumns,
  useMonthSlide,
} from '../components/MonthSlide';
import { formatHours } from '../utils/chartUtils';
import usePermissions from '../hooks/usePermissions';
import './MyWorkplanPage.css';

const isIsoDate = (s) => typeof s === 'string' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(s);

// The server already drops weekends from time off (see buildCanonicalTimeOffPayload), but
// this page rebuilds the day map from the raw payload, so it re-applies the same rule
// rather than trusting the shape. Parsed as UTC to match the server's day arithmetic.
const isWeekdayIsoDate = (s) => {
  if (!isIsoDate(s)) return false;
  const day = new Date(`${s}T00:00:00Z`).getUTCDay();
  return day >= 1 && day <= 5;
};

const formatMonthLabel = (monthStart) => {
  if (!isIsoDate(monthStart)) return '—';
  const [y, m] = monthStart.split('-').map((v) => Number(v));
  if (!Number.isInteger(y) || !Number.isInteger(m)) return '—';
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString(undefined, { month: 'short', year: '2-digit' });
};

const toMonthStartIso = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const addMonthsIso = (monthStart, deltaMonths) => {
  if (!isIsoDate(monthStart)) return null;
  const y = Number(String(monthStart).slice(0, 4));
  const m0 = Number(String(monthStart).slice(5, 7)) - 1;
  if (!Number.isInteger(y) || !Number.isInteger(m0)) return null;
  const d = new Date(y, m0 + Number(deltaMonths || 0), 1);
  d.setHours(0, 0, 0, 0);
  return toMonthStartIso(d);
};

// Same utilization baseline math as ChartsPage:
// workableHours = weekdays*8 - closeDates*8; then scaled by user ftePercent.
const countWeekdaysInclusive = (start, endExclusive) => {
  if (!(start instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  if (a.getTime() >= b.getTime()) return 0;

  let count = 0;
  const cur = new Date(a);
  while (cur.getTime() < b.getTime()) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
};

const parseMonthDay = (raw) => {
  const s = String(raw ?? '').slice(0, 10);
  const m = s.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { month: Number(m[1]), day: Number(m[2]) };
};

// Close dates are stored as recurring month/day definitions, so they are matched against
// each rendered month by month/day the same way the forecast hours page does it.
//
// Weekend occurrences are excluded: the workable baseline is weekdays * 8, so a holiday
// landing on a Saturday never removed a working hour and must not be subtracted again.
const matchCloseDatesInMonth = (monthStartIso, rows) => {
  if (!isIsoDate(monthStartIso)) return [];

  const year = Number(monthStartIso.slice(0, 4));
  const month = Number(monthStartIso.slice(5, 7));
  if (!Number.isInteger(year) || !Number.isInteger(month)) return [];

  const daysInMonth = new Date(year, month, 0).getDate();

  return (Array.isArray(rows) ? rows : [])
    .map((r) => {
      const md = parseMonthDay(r?.closeDate ?? r?.close_date);
      if (!md || md.month !== month || md.day > daysInMonth) return null;

      const d = new Date(year, month - 1, md.day);
      d.setHours(0, 0, 0, 0);
      const dow = d.getDay();
      if (dow === 0 || dow === 6) return null;

      return {
        day: md.day,
        dateIso: `${year}-${String(month).padStart(2, '0')}-${String(md.day).padStart(2, '0')}`,
        dateLabel: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        description: String(r?.description ?? '').trim(),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.day - b.day);
};

const DEFAULT_TIME_OFF_HOURS_PER_DAY = 8;

const SHOW_ASSUMPTIONS_STORAGE_KEY = 'ppc.myWorkplan.showAssumptions';

// "Oct 2026" for the capacity cards; the table headers keep the compact "Oct 26".
const formatMonthLong = (monthStart) => {
  if (!isIsoDate(monthStart)) return '—';
  const [y, m] = monthStart.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'short', year: 'numeric' });
};

// "October" -- the month as it reads in a sentence, for the flag message.
const formatMonthName = (monthStart) => {
  if (!isIsoDate(monthStart)) return '';
  const [y, m] = monthStart.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });
};

// Zero hours are the common case in a sparse plan; a faint dash lets the real figures
// stand out instead of a column of "0.00h".
const HoursCell = ({ hours, className = '' }) => {
  const n = Number(hours) || 0;
  return n === 0 ? (
    <span className={`ppc-mwp-num ppc-mwp-zero ${className}`.trim()}>—</span>
  ) : (
    <span className={`ppc-mwp-num ${className}`.trim()}>{formatHours(n)}</span>
  );
};

const timeOffStatusTone = (status) => {
  const s = String(status || '').toLowerCase();
  if (/approv|confirm/.test(s)) return 'good';
  if (/pend|request|submit|await/.test(s)) return 'warn';
  if (/den|reject|cancel/.test(s)) return 'bad';
  return 'neutral';
};

// One line of a capacity card's arithmetic. The operator sits in its own grey gutter so
// the labels and figures line up whatever the operator is.
const CapacityLine = ({ op = '', label, value, strong = false, ruleAbove = false, tone, title, children }) => (
  <div
    className={[
      'ppc-mwp-cap__line',
      strong ? 'ppc-mwp-cap__line--strong' : '',
      ruleAbove ? 'ppc-mwp-cap__line--rule' : '',
      tone === 'bad' ? 'ppc-mwp-cap__line--bad' : '',
    ].filter(Boolean).join(' ')}
    title={title}
  >
    <span className="ppc-mwp-cap__op" aria-hidden="true">{op}</span>
    <span className="ppc-mwp-cap__label">{label}</span>
    <span className="ppc-mwp-cap__value ppc-mwp-num">{children ?? value}</span>
  </div>
);

// Animates the capacity rail when the window steps one month. Stepping forward, the top card flies
// off to the right while the others glide up (the new middle opening as it goes) and the new month
// flies in at the bottom; stepping back is the mirror image. Uses FLIP: `captureStep()` records where
// every card is just before the month change, then the layout effect animates each card from there to
// its new place. The leaving card is no longer rendered by React, so its detached node is parked in an
// overlay React never touches, and removed once it has flown out.
const RAIL_ANIMATION_ID = 'ppc-mwp-rail-shuffle';

function useCapacityRailShuffle(months) {
  const railRef = useRef(null);
  const fxRef = useRef(null);
  const cardNodesRef = useRef(new Map());
  const pendingStepRef = useRef(null);

  const cardRef = (ms) => (node) => {
    // Keep the last node even after unmount: it is what flies out.
    if (node) cardNodesRef.current.set(ms, node);
  };

  const captureStep = () => {
    const rail = railRef.current;
    if (!rail) return;
    const railBox = rail.getBoundingClientRect();
    const rects = new Map();
    for (const ms of months) {
      const node = cardNodesRef.current.get(ms);
      if (!node?.isConnected) continue;
      // Visual position, including any shuffle still in flight, so a quick second step carries on
      // from where the cards actually are.
      const r = node.getBoundingClientRect();
      rects.set(ms, { left: r.left - railBox.left, top: r.top - railBox.top, width: r.width, height: r.height });
    }
    pendingStepRef.current = { fromMonths: months, rects };
  };

  useLayoutEffect(() => {
    const step = pendingStepRef.current;
    pendingStepRef.current = null;
    const nodes = cardNodesRef.current;
    const rail = railRef.current;
    const fx = fxRef.current;

    const forgetUnusedNodes = () => {
      for (const ms of Array.from(nodes.keys())) if (!months.includes(ms)) nodes.delete(ms);
    };

    const from = step?.fromMonths ?? [];
    let direction = null;
    if (from.length === months.length && from.length > 1) {
      if (months[0] === from[1]) direction = 'next';
      else if (months[1] === from[0]) direction = 'prev';
    }
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (!step || !direction || !rail || !fx || reduceMotion || typeof Element.prototype.animate !== 'function') {
      forgetUnusedNodes();
      return;
    }

    // Settle the previous shuffle first; the captured rects already hold where its cards had got to.
    fx.replaceChildren();
    for (const node of nodes.values()) {
      for (const anim of node.getAnimations?.() ?? []) if (anim.id === RAIL_ANIMATION_ID) anim.cancel();
    }

    const leaving = direction === 'next' ? from[0] : from[from.length - 1];
    const arriving = direction === 'next' ? months[months.length - 1] : months[0];
    const railBox = rail.getBoundingClientRect();

    const leavingNode = nodes.get(leaving);
    const leavingRect = step.rects.get(leaving);
    if (leavingNode && leavingRect && !leavingNode.isConnected) {
      Object.assign(leavingNode.style, {
        position: 'absolute',
        left: `${leavingRect.left}px`,
        top: `${leavingRect.top}px`,
        width: `${leavingRect.width}px`,
        height: `${leavingRect.height}px`,
        margin: '0',
      });
      fx.appendChild(leavingNode);
      const flyOut = leavingNode.animate(
        [
          { transform: 'translateX(0)', opacity: 1 },
          { transform: 'translateX(110%)', opacity: 0 },
        ],
        { id: RAIL_ANIMATION_ID, duration: 240, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
      );
      const dropLeaving = () => leavingNode.remove();
      flyOut.onfinish = dropLeaving;
      flyOut.oncancel = dropLeaving;
    }

    for (const ms of months) {
      if (ms === arriving) continue;
      const node = nodes.get(ms);
      const first = step.rects.get(ms);
      if (!node || !first) continue;
      const r = node.getBoundingClientRect();
      const dx = first.left - (r.left - railBox.left);
      const dy = first.top - (r.top - railBox.top);
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
      node.animate(
        [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
        { id: RAIL_ANIMATION_ID, duration: 320, delay: 60, easing: 'cubic-bezier(0.2, 0, 0, 1)', fill: 'backwards' }
      );
    }

    nodes.get(arriving)?.animate(
      [
        { transform: 'translateX(110%)', opacity: 0 },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      { id: RAIL_ANIMATION_ID, duration: 300, delay: 170, easing: 'cubic-bezier(0, 0, 0.2, 1)', fill: 'backwards' }
    );

    forgetUnusedNodes();
  }, [months]);

  return { railRef, fxRef, cardRef, captureStep };
}

export default function MyWorkplanPage() {
  const navigate = useNavigate();
  const WINDOW_SIZE = 3;
  const WORKPLAN_SELECTED_USER_STORAGE_KEY = 'ppc.myWorkplan.userId';
  const { data: activeTimeOffSource, isLoading: activeTimeOffSourceLoading } = useActiveTimeOffSource();
  const activeTimeOffSourceName = String(activeTimeOffSource?.name || 'manual').toLowerCase();
  const isManualTimeOffSource = activeTimeOffSourceName === 'manual';

  const { data: users, isLoading: usersLoading } = useUsers();
  const { data: closeDates = [] } = useCloseDates();

  const [selectedUserId, setSelectedUserId] = useState(() => {
    try {
      const raw = localStorage.getItem(WORKPLAN_SELECTED_USER_STORAGE_KEY);
      const n = Number(raw);
      return Number.isInteger(n) ? n : null;
    } catch {
      return null;
    }
  });
  const [startMonth, setStartMonth] = useState(() => toMonthStartIso(new Date()));

  // Without workplans.view_others the page shows only the signed-in user's workplan.
  const permissions = usePermissions();
  const canViewOthers = permissions.can('workplans.view_others');
  const myUserId = Number(permissions.user?.id);
  useEffect(() => {
    if (permissions.loaded && !canViewOthers && Number.isInteger(myUserId) && selectedUserId !== myUserId) {
      setSelectedUserId(myUserId);
    }
  }, [permissions.loaded, canViewOthers, myUserId, selectedUserId]);

  useEffect(() => {
    try {
      if (Number.isInteger(selectedUserId)) {
        localStorage.setItem(WORKPLAN_SELECTED_USER_STORAGE_KEY, String(selectedUserId));
      } else {
        localStorage.removeItem(WORKPLAN_SELECTED_USER_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  }, [selectedUserId]);

  const months = useMemo(() => {
    const list = [];
    for (let i = 0; i < WINDOW_SIZE; i += 1) {
      const ms = addMonthsIso(startMonth, i);
      if (ms) list.push(ms);
    }
    return list;
  }, [WINDOW_SIZE, startMonth]);

  // The months either side of the window, parked just out of view for the month slide.
  const prevMonth = useMemo(() => addMonthsIso(startMonth, -1), [startMonth]);
  const nextMonth = useMemo(() => addMonthsIso(startMonth, WINDOW_SIZE), [WINDOW_SIZE, startMonth]);

  // Whole years around the window, so stepping a month reuses data that is already loaded. Rows are
  // filtered back down to the displayed months below.
  const fetchMonths = useMemo(() => getMonthSlideFetchMonths(startMonth, WINDOW_SIZE), [WINDOW_SIZE, startMonth]);

  const monthRangeLabel = useMemo(() => {
    if (months.length === 0) return '';
    const first = formatMonthLabel(months[0]);
    const last = formatMonthLabel(months[months.length - 1]);
    return first === last ? first : `${first} – ${last}`;
  }, [months]);

  const currentMonthStart = useMemo(() => toMonthStartIso(new Date()), []);
  const isOnCurrentWindow = startMonth === currentMonthStart;

  const userList = useMemo(() => {
    const list = Array.isArray(users) ? users : users?.users;
    const arr = Array.isArray(list) ? list : [];
    return arr
      .map((u) => ({
        id: Number(u?.id),
        name: String(u?.name || u?.displayName || u?.email || `User ${u?.id}`),
        ftePercent: Number(u?.ftePercent ?? u?.fte_percent ?? 100),
      }))
      .filter((u) => Number.isInteger(u.id))
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [users]);

  const selectedUser = useMemo(
    () => userList.find((u) => Number(u?.id) === Number(selectedUserId)) || null,
    [selectedUserId, userList]
  );

  const selectedUserFteScale = useMemo(() => {
    const raw = Number(selectedUser?.ftePercent);
    const fte = Number.isFinite(raw) ? raw : 100;
    return Math.max(0, fte / 100);
  }, [selectedUser?.ftePercent]);

  // Reserved hours are stored as their inverse on the user (ftePercent = 100 - reserved), so the
  // Reserved hours row names the percentage the user was actually configured with.
  const selectedUserReservedPercent = useMemo(() => {
    const reserved = Math.min(100, Math.max(0, 100 - selectedUserFteScale * 100));
    return Number.isInteger(reserved) ? String(reserved) : reserved.toFixed(2);
  }, [selectedUserFteScale]);

  const userPickerData = useMemo(() => userList.map((u) => ({ label: u.name, value: u.id })), [userList]);

  const {
    data: workplan,
    isLoading: workplanLoading,
    error: workplanError,
  } = useUserWorkplan(Number.isInteger(selectedUserId) ? selectedUserId : null, fetchMonths);

  const {
    data: myTimeOff,
    isLoading: myTimeOffLoading,
    error: myTimeOffError,
  } = useUserTimeOffDays(Number.isInteger(selectedUserId) ? selectedUserId : null);

  const timeOffHoursByDay = useMemo(() => {
    const out = {};

    const mapDay = (rawDay, rawHours) => {
      const day = String(rawDay || '').slice(0, 10);
      if (!isWeekdayIsoDate(day)) return;
      const hours = Number(rawHours);
      out[day] = Number.isFinite(hours) && hours >= 0 ? hours : DEFAULT_TIME_OFF_HOURS_PER_DAY;
    };

    if (myTimeOff && typeof myTimeOff === 'object' && myTimeOff.time_off_hours_by_day && typeof myTimeOff.time_off_hours_by_day === 'object') {
      Object.entries(myTimeOff.time_off_hours_by_day).forEach(([day, hours]) => mapDay(day, hours));
    }

    if (Array.isArray(myTimeOff?.entries)) {
      myTimeOff.entries.forEach((entry) => {
        mapDay(entry?.date ?? entry?.time_off_date, entry?.hours);
      });
    }

    const rawDays = Array.isArray(myTimeOff?.time_off_days)
      ? myTimeOff.time_off_days
      : Array.isArray(myTimeOff?.vacation_days)
        ? myTimeOff.vacation_days
        : [];

    rawDays.forEach((rawDay) => {
      const day = String(rawDay || '').slice(0, 10);
      if (!isWeekdayIsoDate(day)) return;
      if (!Object.prototype.hasOwnProperty.call(out, day)) {
        out[day] = DEFAULT_TIME_OFF_HOURS_PER_DAY;
      }
    });

    return out;
  }, [myTimeOff]);

  const upcomingTimeOffDays = useMemo(() => {
    return Object.keys(timeOffHoursByDay).sort((a, b) => String(a).localeCompare(String(b)));
  }, [timeOffHoursByDay]);

  // Which booking each day belongs to, when the source reports it. EmployeeConnect does not
  // yet send request identity, so this is usually empty and the grouping below falls back to
  // runs of consecutive working days.
  const timeOffRequestByDay = useMemo(() => {
    const out = {};
    for (const entry of Array.isArray(myTimeOff?.entries) ? myTimeOff.entries : []) {
      const day = String(entry?.date ?? entry?.time_off_date ?? '').slice(0, 10);
      if (!isWeekdayIsoDate(day)) continue;
      const requestId = entry?.request_id ?? entry?.requestId;
      if (requestId === undefined || requestId === null || String(requestId).trim() === '') continue;
      out[day] = {
        requestId: String(requestId).trim(),
        requestType: entry?.request_type ?? entry?.requestType ?? null,
        status: entry?.status ?? null,
      };
    }
    return out;
  }, [myTimeOff]);

  // Upcoming time off as month -> bookings -> days, so a two-week vacation reads as one line
  // instead of ten pills. Without request identity from the source, a booking is inferred as
  // a run of consecutive working days (a weekend between Friday and Monday does not break it).
  const timeOffMonthGroups = useMemo(() => {
    const days = upcomingTimeOffDays;
    if (days.length === 0) return [];

    const isNextWorkingDay = (prevIso, nextIso) => {
      const prev = new Date(`${prevIso}T00:00:00Z`);
      const gapDays = Math.round((new Date(`${nextIso}T00:00:00Z`) - prev) / 86400000);
      if (gapDays === 1) return true;
      // Friday -> Monday is still consecutive working time.
      return prev.getUTCDay() === 5 && gapDays === 3;
    };

    const byMonth = new Map();

    for (const day of days) {
      const monthStart = `${day.slice(0, 7)}-01`;
      if (!byMonth.has(monthStart)) byMonth.set(monthStart, []);
      const groups = byMonth.get(monthStart);

      const request = timeOffRequestByDay?.[day] ?? null;
      const last = groups[groups.length - 1];
      const lastDay = last ? last.days[last.days.length - 1].date : null;

      // A booking never spans months here: the panel mirrors the per-month Time Off row,
      // so a request crossing a month boundary shows its portion under each month.
      const continuesLast = last
        && (request
          ? last.requestId === request.requestId
          : !last.requestId && isNextWorkingDay(lastDay, day));

      const hours = Number(timeOffHoursByDay?.[day] ?? 0);
      if (continuesLast) {
        last.days.push({ date: day, hours });
        last.totalHours += hours;
      } else {
        groups.push({
          key: `${monthStart}|${request?.requestId ?? day}`,
          requestId: request?.requestId ?? null,
          requestType: request?.requestType ?? null,
          status: request?.status ?? null,
          days: [{ date: day, hours }],
          totalHours: hours,
        });
      }
    }

    return Array.from(byMonth.entries())
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
      .map(([monthStart, groups]) => ({
        monthStart,
        groups,
        totalHours: groups.reduce((sum, g) => sum + g.totalHours, 0),
        dayCount: groups.reduce((sum, g) => sum + g.days.length, 0),
      }));
  }, [timeOffHoursByDay, timeOffRequestByDay, upcomingTimeOffDays]);

  const formatDateLabel = (isoDate) => {
    if (!isIsoDate(isoDate)) return String(isoDate || '');
    const y = Number(isoDate.slice(0, 4));
    const m = Number(isoDate.slice(5, 7));
    const d = Number(isoDate.slice(8, 10));
    const dt = new Date(y, m - 1, d);
    // No year: each card sits under its month's heading, and dropping it keeps a
    // "Mon, Dec 28 – Thu, Dec 31" range on one line in a quarter-width column.
    return dt.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Activities with hours in the displayed months (the fetch covers whole years).
  const rows = useMemo(() => {
    const r = (Array.isArray(workplan?.rows) ? workplan.rows : []).filter((row) =>
      months.some((ms) => Number(row?.hours?.[ms] ?? 0) !== 0)
    );
    return r.sort((a, b) => {
      const pa = String(a?.projectName || '');
      const pb = String(b?.projectName || '');
      if (pa !== pb) return pa.localeCompare(pb);
      const ta = String(a?.taskName || '');
      const tb = String(b?.taskName || '');
      if (ta !== tb) return ta.localeCompare(tb);
      // Within a task, the task's own activity order (#1, #2, ...), as Discipline Details and the
      // forecast page list them, so the three line up.
      const na = Number(a?.activityNumber ?? a?.activityId);
      const nb = Number(b?.activityNumber ?? b?.activityId);
      return na - nb || String(a?.activityName || '').localeCompare(String(b?.activityName || ''));
    });
  }, [months, workplan?.rows]);

  // Rows arrive sorted project -> task -> activity, so a group is a run of rows sharing
  // a project and task. Each group carries its own per-month subtotals for the header row.
  const groups = useMemo(() => {
    const out = [];
    for (const r of rows) {
      const key = `${String(r?.projectId ?? r?.projectName ?? '')}|${String(r?.taskId ?? r?.taskName ?? '')}`;
      let g = out[out.length - 1];
      if (!g || g.key !== key) {
        g = {
          key,
          projectId: Number(r?.projectId),
          projectName: String(r?.projectName || ''),
          taskId: Number(r?.taskId),
          taskName: String(r?.taskName || ''),
          rows: [],
          subtotals: {},
        };
        for (const ms of fetchMonths) g.subtotals[ms] = 0;
        out.push(g);
      }
      g.rows.push(r);
      // Covers the neighbouring months too, which the month slide shows on its way in.
      for (const ms of fetchMonths) g.subtotals[ms] += Number(r?.hours?.[ms] ?? 0);
    }
    return out;
  }, [fetchMonths, rows]);

  const projectCount = useMemo(
    () => new Set(groups.map((g) => g.projectId || g.projectName)).size,
    [groups]
  );

  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const anyGroupOpen = groups.some((g) => !collapsedGroups.has(g.key));
  const toggleGroup = (key) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleAllGroups = () =>
    setCollapsedGroups(anyGroupOpen ? new Set(groups.map((g) => g.key)) : new Set());

  const [showAssumptions, setShowAssumptions] = useState(() => {
    try {
      return localStorage.getItem(SHOW_ASSUMPTIONS_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SHOW_ASSUMPTIONS_STORAGE_KEY, showAssumptions ? '1' : '0');
    } catch {
      // ignore
    }
  }, [showAssumptions]);

  // Capacity cards collapse one at a time, to a single line of month and utilization. The last month
  // in the window starts collapsed; once a month is toggled it keeps that state (keyed by month) as the
  // window steps past it.
  const [capCollapsedByMonth, setCapCollapsedByMonth] = useState(() => new Map());
  const isCapMonthCollapsed = (ms) =>
    capCollapsedByMonth.has(ms) ? capCollapsedByMonth.get(ms) : ms === months[months.length - 1];
  const toggleCapMonth = (ms) => {
    const collapsed = isCapMonthCollapsed(ms);
    setCapCollapsedByMonth((prev) => new Map(prev).set(ms, !collapsed));
  };

  // Flags: the lead each discipline's flag goes to, and what has already been flagged.
  // The server answers enabled:false until its migration has run; the feature is hidden
  // rather than shown broken.
  const { data: flagsData } = useUserWorkplanFlags(
    Number.isInteger(selectedUserId) ? selectedUserId : null,
    fetchMonths
  );
  const flagsEnabled = flagsData?.enabled === true;
  const flagByTaskMonth = useMemo(() => {
    const out = {};
    for (const f of Array.isArray(flagsData?.flags) ? flagsData.flags : []) {
      out[`${f.taskId}|${f.monthStart}`] = f;
    }
    return out;
  }, [flagsData?.flags]);

  // The target outlives the open flag so the dialog keeps its content while it fades out.
  const [flagTarget, setFlagTarget] = useState(null);
  const [flagOpen, setFlagOpen] = useState(false);
  const openFlag = (group, ms) => {
    const lead = flagsData?.leads?.[String(group.taskId)] ?? null;
    if (!lead) return;
    setFlagTarget({
      userId: selectedUserId,
      projectName: group.projectName,
      taskId: group.taskId,
      taskName: group.taskName,
      monthStart: ms,
      monthName: formatMonthName(ms),
      hours: group.subtotals[ms] ?? 0,
      activities: group.rows.map((r) => ({ name: String(r?.activityName || '—'), hours: Number(r?.hours?.[ms] ?? 0) })),
      lead,
    });
    setFlagOpen(true);
  };

  const totalsByMonth = useMemo(() => {
    const totals = {};
    for (const ms of months) totals[ms] = 0;
    for (const r of rows) {
      for (const ms of months) {
        totals[ms] += Number(r?.hours?.[ms] ?? 0);
      }
    }
    return totals;
  }, [months, rows]);

  const timeOffHoursByMonth = useMemo(() => {
    const out = {};
    for (const ms of months) out[ms] = 0;

    for (const [day, rawHours] of Object.entries(timeOffHoursByDay)) {
      if (!isIsoDate(day)) continue;
      const monthStart = `${day.slice(0, 7)}-01`;
      if (!Object.prototype.hasOwnProperty.call(out, monthStart)) continue;
      const hours = Number(rawHours);
      if (!Number.isFinite(hours) || hours <= 0) continue;
      out[monthStart] += hours;
    }

    return out;
  }, [months, timeOffHoursByDay]);

  // The one source of truth for close dates: the utilization math and the Close Dates row
  // both read this, so the number shown can never disagree with the number subtracted.
  const closeDatesByMonth = useMemo(() => {
    const out = {};
    for (const ms of months) out[ms] = matchCloseDatesInMonth(ms, closeDates);
    return out;
  }, [closeDates, months]);

  const closeDateHoursByMonth = useMemo(() => {
    const out = {};
    for (const ms of months) {
      out[ms] = (closeDatesByMonth?.[ms]?.length ?? 0) * DEFAULT_TIME_OFF_HOURS_PER_DAY;
    }
    return out;
  }, [closeDatesByMonth, months]);

  // Every step of the utilization math for one month, in the order the summary rows print it:
  //
  //   working days * 8                                  -> totalHours
  //   totalHours - close dates                          -> availableHours
  //   (availableHours - time off) * (1 - reserved)       -> maxBillableHours
  //   scheduledHours / maxBillableHours * 100            -> utilization
  //
  // Order of operations matters on the third line: reserved hours are a share of the time the
  // user is actually at work, so scaling before time off is removed would charge the reduction
  // against days they were never going to work. Keeping the whole chain in one memo means the
  // rows on screen are the same numbers the percentage was computed from -- a reader can check
  // the bottom line by reading down the column.
  const scheduleByMonth = useMemo(() => {
    const reservedScale = Math.max(0, 1 - selectedUserFteScale);
    const out = {};

    for (const ms of months) {
      let totalHours = 0;

      if (isIsoDate(ms)) {
        const y = Number(ms.slice(0, 4));
        const m0 = Number(ms.slice(5, 7)) - 1;

        if (Number.isInteger(y) && Number.isInteger(m0)) {
          const start = new Date(y, m0, 1);
          start.setHours(0, 0, 0, 0);
          const endExclusive = new Date(y, m0 + 1, 1);
          endExclusive.setHours(0, 0, 0, 0);
          totalHours = countWeekdaysInclusive(start, endExclusive) * 8;
        }
      }

      const rawCloseHours = Number(closeDateHoursByMonth?.[ms] ?? 0);
      const closeDateHours = Number.isFinite(rawCloseHours) ? Math.max(0, rawCloseHours) : 0;
      const availableHours = Math.max(0, totalHours - closeDateHours);

      const rawTimeOff = Number(timeOffHoursByMonth?.[ms] ?? 0);
      const timeOffHours = Number.isFinite(rawTimeOff) ? Math.max(0, rawTimeOff) : 0;
      const afterTimeOff = Math.max(0, availableHours - timeOffHours);

      // Shown as its own subtracted line, so availableHours - timeOff - reserved reads across
      // to maxBillableHours exactly.
      const reservedHours = afterTimeOff * reservedScale;
      const maxBillableHours = afterTimeOff * selectedUserFteScale;

      const rawScheduled = Number(totalsByMonth?.[ms] ?? 0);
      const scheduledHours = Number.isFinite(rawScheduled) ? rawScheduled : 0;

      const utilization =
        maxBillableHours > 0 && scheduledHours > 0 ? (scheduledHours / maxBillableHours) * 100 : 0;

      out[ms] = {
        totalHours,
        closeDateHours,
        availableHours,
        timeOffHours,
        reservedHours,
        maxBillableHours,
        scheduledHours,
        utilization,
      };
    }

    return out;
  }, [closeDateHoursByMonth, months, selectedUserFteScale, timeOffHoursByMonth, totalsByMonth]);

  const prefetchUserWorkplanMonths = usePrefetchUserWorkplanMonths();
  const capacityRail = useCapacityRailShuffle(months);

  const { containerRef: monthSlideContainerRef, stepMonths } = useMonthSlide({
    start: startMonth,
    setStart: (target) => {
      // Record where the capacity cards are, so the rail can animate from there once the window moves.
      capacityRail.captureStep();
      setStartMonth(target);
    },
    prepare: (target) => prefetchUserWorkplanMonths(selectedUserId, getMonthSlideFetchMonths(target, WINDOW_SIZE)),
  });

  // Keep the months a few steps either way loaded, so stepping through them never waits on the network.
  useEffect(() => {
    for (const list of getMonthSlidePrefetchMonthLists(startMonth, WINDOW_SIZE)) {
      prefetchUserWorkplanMonths(selectedUserId, list);
    }
  }, [WINDOW_SIZE, prefetchUserWorkplanMonths, selectedUserId, startMonth]);

  const goPrevMonths = () => stepMonths(-1);
  const goNextMonths = () => stepMonths(1);

  const goCurrentMonths = () => {
    setStartMonth(currentMonthStart);
  };

  const goAddTimeOff = () => {
    if (isManualTimeOffSource) {
      navigate('/configuration/time-off');
      return;
    }
    window.location.assign('https://employeeconnect.nsieng.com/time-off/create');
  };

  return (
    <div className="ppc-page ppc-mwp">
      <section className="ppc-mwp-section">
        <Stack justifyContent="space-between" alignItems="center" wrap spacing={10}>
          <Stack spacing={10} alignItems="center" wrap>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 className="ppc-mwp-title">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <i className="bi bi-calendar-check" aria-hidden="true" />
                    My Workplan
                  </span>
                </h3>
              </div>
            </div>

            <div className="ppc-mwp-picker-wrap">
              <span className="ppc-mwp-picker-label">Employee</span>
              {canViewOthers ? (
                <SelectPicker
                  className="ppc-mwp-picker"
                  data={userPickerData}
                  value={selectedUserId}
                  onChange={(v) => setSelectedUserId(v ?? null)}
                  placeholder={usersLoading ? 'Loading users…' : 'Select a user'}
                  style={{ minWidth: 260 }}
                  cleanable
                  searchable
                />
              ) : null}
            </div>
          </Stack>
        </Stack>

        {/* The header rule the other pages have; the section's 10px gap plus 14px matches their 24px. */}
        <Divider style={{ margin: '14px 0' }} />

        {!Number.isInteger(selectedUserId) ? (
          <Message type="info" showIcon>
            Select a user to view forecast hours.
          </Message>
        ) : workplanError ? (
          <Message type="error" showIcon>
            Failed to load workplan: {workplanError?.message || String(workplanError)}
          </Message>
        ) : null}

      </section>

      <div className="ppc-mwp-layout">
        <section ref={monthSlideContainerRef} className="ppc-mwp-plan" aria-label="Workplan">
          <div className="ppc-mwp-toolbar">
            {rows.length > 0 ? (
              <span className="ppc-mwp-toolbar__summary">
                {`${projectCount} project${projectCount === 1 ? '' : 's'} · ${rows.length} activit${rows.length === 1 ? 'y' : 'ies'}`}
              </span>
            ) : null}
            {flagsEnabled && rows.length > 0 ? (
              <span className="ppc-mwp-toolbar__hint">
                <i className="bi bi-flag" aria-hidden="true" />
                Concerned about a month? Flag it to your lead.
              </span>
            ) : null}
            <div className="ppc-mwp-toolbar__spacer" />
            <div className="ppc-mwp-controls" role="group" aria-label="Months shown">
              <IconButton
                appearance="subtle"
                size="sm"
                icon={<i className="bi bi-chevron-left" aria-hidden="true" />}
                aria-label="Previous month"
                title="Previous month"
                onClick={goPrevMonths}
              />
              <div className="ppc-mwp-range-label">
                {monthRangeLabel}
              </div>
              <IconButton
                appearance="subtle"
                size="sm"
                icon={<i className="bi bi-chevron-right" aria-hidden="true" />}
                aria-label="Next month"
                title="Next month"
                onClick={goNextMonths}
              />
              <Button appearance="subtle" size="sm" onClick={goCurrentMonths} disabled={isOnCurrentWindow}>
                Current
              </Button>
            </div>
            <span className="ppc-mwp-toolbar__divider" aria-hidden="true" />
            <label className="ppc-mwp-toolbar__toggle">
              <Toggle size="sm" checked={showAssumptions} onChange={(v) => setShowAssumptions(v)} />
              <span>Show assumptions</span>
            </label>
            {groups.length > 0 ? (
              <button type="button" className="ppc-mwp-linkbtn" onClick={toggleAllGroups}>
                {anyGroupOpen ? 'Collapse all' : 'Expand all'}
              </button>
            ) : null}
          </div>

          <div className="ppc-mwp-table-shell">
            <div className="ppc-mwp-table-scroll">
              <div
                className="ppc-mwp-grid ppc-month-slide-grid"
                role="table"
                aria-label="Forecast hours by activity and month"
                style={{ gridTemplateColumns: monthSlideColumns('minmax(240px, 1fr)', months, 'minmax(120px, 132px)') }}
              >
                <div className="ppc-mwp-tr" role="row">
                  <div className="ppc-mwp-th ppc-month-slide-pin" role="columnheader">Activity</div>
                  <MonthSlideCells
                    months={months}
                    prevMonth={prevMonth}
                    nextMonth={nextMonth}
                    renderCell={(ms) => (
                      <div key={ms} className="ppc-mwp-th ppc-mwp-th--num" role="columnheader">
                        {formatMonthLabel(ms)}
                      </div>
                    )}
                  />
                </div>

                {workplanLoading ? (
                  <div className="ppc-mwp-empty">Loading workplan…</div>
                ) : rows.length === 0 && Number.isInteger(selectedUserId) ? (
                  <div className="ppc-mwp-empty">No forecast hours in this window.</div>
                ) : (
                  groups.map((g) => {
                    const open = !collapsedGroups.has(g.key);
                    const lead = flagsData?.leads?.[String(g.taskId)] ?? null;
                    const bodyId = `ppc-mwp-group-${g.key.replace(/[^a-zA-Z0-9]/g, '-')}`;

                    return (
                      <Fragment key={g.key}>
                        {/* The whole row toggles for mouse users; the chevron is the real
                            button for keyboard and screen readers. The task link and the
                            flag buttons stop propagation so they never collapse the group. */}
                        <div
                          className="ppc-mwp-tr ppc-mwp-group"
                          role="row"
                          onClick={() => toggleGroup(g.key)}
                        >
                          <div className="ppc-mwp-td ppc-mwp-group__name ppc-month-slide-pin" role="rowheader">
                            <button
                              type="button"
                              className="ppc-mwp-chevron"
                              aria-expanded={open}
                              aria-controls={bodyId}
                              aria-label={`${open ? 'Collapse' : 'Expand'} ${g.taskName}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleGroup(g.key);
                              }}
                            >
                              <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'}`} aria-hidden="true" />
                            </button>
                            <div className="ppc-mwp-group__titles">
                              <span className="ppc-mwp-group__project">{g.projectName}</span>
                              {Number.isInteger(g.taskId) ? (
                                <Link
                                  className="ppc-mwp-group__task"
                                  to={`/tasks/${g.taskId}`}
                                  state={{ backTo: '/my-workplan' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {g.taskName}
                                </Link>
                              ) : (
                                <span className="ppc-mwp-group__task">{g.taskName}</span>
                              )}
                            </div>
                            <span className="ppc-mwp-count">
                              {`${g.rows.length} activit${g.rows.length === 1 ? 'y' : 'ies'}`}
                            </span>
                          </div>

                          <MonthSlideCells
                            months={months}
                            prevMonth={prevMonth}
                            nextMonth={nextMonth}
                            renderCell={(ms) => {
                            const flag = flagByTaskMonth[`${g.taskId}|${ms}`] ?? null;
                            const flagLabel = !lead
                              ? 'No discipline lead or project PIC to flag this to'
                              : flag
                                ? `Flagged to ${flag.leadName || lead.name}${flag.createdAt ? ` on ${new Date(flag.createdAt).toLocaleDateString()}` : ''} · ${flag.reasonLabel}. Click to flag again.`
                                : `Flag ${formatMonthName(ms)} hours to ${lead.name}`;

                            return (
                              <div key={ms} className="ppc-mwp-td ppc-mwp-td--num ppc-mwp-group__total" role="cell">
                                {flagsEnabled ? (
                                  <Whisper trigger="hover" placement="top" speaker={<Tooltip>{flagLabel}</Tooltip>}>
                                    <span className="ppc-mwp-flagwrap">
                                      <button
                                        type="button"
                                        className={`ppc-mwp-flagbtn${flag ? ' ppc-mwp-flagbtn--flagged' : ''}`}
                                        aria-label={flagLabel}
                                        disabled={!lead}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openFlag(g, ms);
                                        }}
                                      >
                                        <i className={`bi ${flag ? 'bi-flag-fill' : 'bi-flag'}`} aria-hidden="true" />
                                      </button>
                                    </span>
                                  </Whisper>
                                ) : null}
                                <HoursCell hours={g.subtotals[ms]} />
                              </div>
                            );
                            }}
                          />
                        </div>

                        {open ? (
                          <div id={bodyId} role="rowgroup" style={{ display: 'contents' }}>
                            {g.rows.map((r) => {
                              const assumption = String(r?.activityAssumptions || '').trim();
                              return (
                                <div key={`${r.taskId}|${r.activityId}`} className="ppc-mwp-tr ppc-mwp-act" role="row">
                                  <div className="ppc-mwp-td ppc-mwp-act__name ppc-month-slide-pin" role="rowheader">
                                    <div className="ppc-mwp-act__line">
                                      <span>
                                        {Number.isInteger(Number(r?.activityNumber)) && r?.activityNumber !== null
                                          ? `#${r.activityNumber} `
                                          : ''}
                                        {r?.activityName || '—'}
                                      </span>
                                      {assumption && !showAssumptions ? (
                                        <Whisper
                                          trigger="hover"
                                          placement="top"
                                          enterable
                                          speaker={
                                            <Tooltip>
                                              <div className="ppc-mwp-tip">{assumption}</div>
                                            </Tooltip>
                                          }
                                        >
                                          <span className="ppc-mwp-info" aria-label={`Assumption: ${assumption}`} tabIndex={0}>
                                            <i className="bi bi-info-circle" aria-hidden="true" />
                                          </span>
                                        </Whisper>
                                      ) : null}
                                    </div>
                                    {assumption && showAssumptions ? (
                                      <div className="ppc-mwp-act__assumption">{assumption}</div>
                                    ) : null}
                                  </div>
                                  <MonthSlideCells
                                    months={months}
                                    prevMonth={prevMonth}
                                    nextMonth={nextMonth}
                                    renderCell={(ms) => (
                                      <div key={ms} className="ppc-mwp-td ppc-mwp-td--num" role="cell">
                                        <HoursCell hours={r?.hours?.[ms]} />
                                      </div>
                                    )}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </section>

        {Number.isInteger(selectedUserId) && months.length > 0 ? (
          <aside ref={capacityRail.railRef} className="ppc-mwp-rail" aria-label="Capacity and utilization">
            <div className="ppc-mwp-label">Capacity &amp; utilization</div>
            {months.map((ms) => {
              const m = scheduleByMonth?.[ms] ?? {};
              const net = Number(m.maxBillableHours ?? 0);
              const scheduled = Number(m.scheduledHours ?? 0);
              const utilization = Number(m.utilization ?? 0);
              const overBy = scheduled - net;
              const isOver = overBy > 0.005;
              const scale = Math.max(net, scheduled);
              const fillPct = scale > 0 ? (Math.min(net, scheduled) / scale) * 100 : 0;
              const overPct = scale > 0 && isOver ? (overBy / scale) * 100 : 0;
              const monthCloseDates = closeDatesByMonth?.[ms] ?? [];
              const closeTooltip = monthCloseDates.length > 0
                ? monthCloseDates.map((cd) => (cd.description ? `${cd.dateLabel} — ${cd.description}` : cd.dateLabel)).join('\n')
                : 'No close dates this month.';

              const utilBar = (
                <div
                  className="ppc-mwp-bar"
                  role="img"
                  aria-label={`${Math.round(utilization)}% utilized`}
                >
                  <div className="ppc-mwp-bar__fill" style={{ width: `${fillPct}%` }} />
                  <div className="ppc-mwp-bar__over" style={{ width: `${overPct}%` }} />
                </div>
              );
              const utilPct = (
                <span className={`ppc-mwp-cap__util-pct ppc-mwp-num${utilization > 100 ? ' ppc-mwp-cap__util-pct--bad' : ''}`}>
                  {`${Math.round(utilization)}%`}
                </span>
              );

              const collapsed = isCapMonthCollapsed(ms);

              // One structure for both states, so collapsing animates: the details shrink away while a
              // one-line summary (bar and %) fades in beside the month.
              return (
                <div key={ms} ref={capacityRail.cardRef(ms)} className={`ppc-mwp-cap${collapsed ? ' ppc-mwp-cap--collapsed' : ''}`}>
                  <div className="ppc-mwp-cap__head">
                    <span className="ppc-mwp-cap__month">{formatMonthLong(ms)}</span>
                    <span
                      className="ppc-mwp-cap__summary"
                      aria-hidden={!collapsed}
                      title={`${formatHours(scheduled)} scheduled of ${formatHours(net)} net available`}
                    >
                      {utilBar}
                      {utilPct}
                    </span>
                    {isOver ? (
                      <span className="ppc-mwp-pill ppc-mwp-pill--bad ppc-mwp-num ppc-mwp-cap__over">{`+${formatHours(overBy)} over`}</span>
                    ) : null}
                    <button
                      type="button"
                      className="ppc-mwp-cap__toggle"
                      aria-expanded={!collapsed}
                      aria-label={`${collapsed ? 'Show' : 'Hide'} ${formatMonthLong(ms)} capacity details`}
                      onClick={() => toggleCapMonth(ms)}
                    >
                      <i className="bi bi-chevron-up" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="ppc-mwp-cap__body" inert={collapsed}>
                    <div className="ppc-mwp-cap__body-inner">
                      <div className="ppc-mwp-cap__calc">
                        <CapacityLine
                          label="Hours in month"
                          value={formatHours(m.totalHours ?? 0)}
                          strong
                          title="Working days in the month x 8 hours. Weekends are not counted."
                        />
                        <CapacityLine op="−" label="Close dates">
                          <Whisper
                            trigger="hover"
                            placement="left"
                            enterable
                            speaker={<Tooltip><div className="ppc-mwp-tip">{closeTooltip}</div></Tooltip>}
                          >
                            <span className="ppc-mwp-help">{formatHours(m.closeDateHours ?? 0)}</span>
                          </Whisper>
                        </CapacityLine>
                        <CapacityLine
                          op="="
                          label="Available"
                          value={formatHours(m.availableHours ?? 0)}
                          strong
                          ruleAbove
                          title="Hours in month less company close dates."
                        />
                        <CapacityLine
                          op="−"
                          label="Time off"
                          value={myTimeOffLoading ? '…' : myTimeOffError ? '—' : formatHours(m.timeOffHours ?? 0)}
                          title="Approved and upcoming time off hours in this month."
                        />
                        <CapacityLine
                          op="−"
                          label="Reserved"
                          value={formatHours(m.reservedHours ?? 0)}
                          title={`${selectedUserReservedPercent}% of the hours left after time off, held back for admin and non-billable work.`}
                        />
                        <CapacityLine
                          op="="
                          label="Net available"
                          value={formatHours(net)}
                          strong
                          ruleAbove
                          title="Available less time off and reserved hours."
                        />
                        <CapacityLine
                          label="Scheduled"
                          value={formatHours(scheduled)}
                          strong
                          tone={isOver ? 'bad' : undefined}
                          title="Forecast hours across every activity on this workplan."
                        />
                      </div>

                      <div className="ppc-mwp-cap__util" title="Scheduled ÷ net available">
                        <span className="ppc-mwp-cap__util-label">Utilization</span>
                        {utilBar}
                        {utilPct}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Where a card that has left the window plays out its exit; React never renders into it. */}
            <div ref={capacityRail.fxRef} className="ppc-mwp-rail__fx" aria-hidden="true" />
          </aside>
        ) : null}

        <section className="ppc-mwp-timeoff" aria-labelledby="ppc-mwp-timeoff-title">
          <div className="ppc-mwp-section-head">
            <h4 className="ppc-mwp-section-title" id="ppc-mwp-timeoff-title">Upcoming time off</h4>
            <button
              type="button"
              className="ppc-mwp-btn ppc-mwp-btn--sm"
              title={isManualTimeOffSource ? 'Add time off' : 'Request time off in EmployeeConnect'}
              disabled={activeTimeOffSourceLoading}
              onClick={goAddTimeOff}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" />
              Request time off
            </button>
          </div>

          {!Number.isInteger(selectedUserId) ? (
            <Message type="info" showIcon>
              Select a user to view upcoming time off days.
            </Message>
          ) : myTimeOffLoading ? (
            <div className="ppc-mwp-muted">Loading time off days…</div>
          ) : myTimeOffError ? (
            <Message type="warning" showIcon>
              Failed to load upcoming time off days.
            </Message>
          ) : upcomingTimeOffDays.length === 0 ? (
            <p className="ppc-mwp-emptybox">No upcoming time off.</p>
          ) : (
            <div className="ppc-mwp-timeoff-grid">
              {timeOffMonthGroups.map((month) => (
                <div key={month.monthStart} className="ppc-mwp-timeoff-month">
                  <div className="ppc-mwp-timeoff-month__head">
                    <span className="ppc-mwp-timeoff-month__name">{formatMonthLong(month.monthStart)}</span>
                    <span className="ppc-mwp-timeoff-month__total ppc-mwp-num">
                      {`${month.dayCount} day${month.dayCount === 1 ? '' : 's'} · ${formatHours(month.totalHours)}`}
                    </span>
                  </div>

                  {month.groups.map((group) => {
                    const first = group.days[0];
                    const last = group.days[group.days.length - 1];
                    const rangeLabel =
                      group.days.length === 1
                        ? formatDateLabel(first.date)
                        : `${formatDateLabel(first.date)} – ${formatDateLabel(last.date)}`;
                    const tooltipText = [
                      ...(group.requestType ? [String(group.requestType)] : []),
                      ...group.days.map((d) => `${formatDateLabel(d.date)} — ${formatHours(d.hours)}`),
                    ].join('\n');

                    return (
                      <Whisper
                        key={group.key}
                        trigger="hover"
                        placement="top"
                        enterable
                        speaker={
                          <Tooltip>
                            <div className="ppc-mwp-tip">{tooltipText}</div>
                          </Tooltip>
                        }
                      >
                        <div className="ppc-mwp-timeoff-card">
                          <span className="ppc-mwp-timeoff-card__range">{rangeLabel}</span>
                          <div className="ppc-mwp-timeoff-card__meta">
                            <span className="ppc-mwp-num">
                              {`${group.days.length} day${group.days.length === 1 ? '' : 's'} · ${formatHours(group.totalHours)}`}
                            </span>
                            {group.status ? (
                              <span className={`ppc-mwp-status ppc-mwp-status--${timeOffStatusTone(group.status)}`}>
                                <span className="ppc-mwp-status__dot" aria-hidden="true" />
                                {group.status}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </Whisper>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <FlagHoursModal
        open={flagOpen}
        target={flagTarget}
        employeeName={selectedUser?.name || ''}
        onClose={() => setFlagOpen(false)}
      />
    </div>
  );
}
